import { useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Formik, Form } from 'formik'
import * as yup from 'yup'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    LinearProgress,
    makeStyles,
    Box,
    TextField
} from '@material-ui/core'
import AddBoxIcon from '@material-ui/icons/AddBox'
import CloseIcon from '@material-ui/icons/Close'

import { ConnectedSelect, ConnectedInput } from 'UI/Components'

import {
    useDataMutation,
    useNotification,
    useAuth,
    useOrganization,
    ROLES_LIST,
    ROLE,
    useRoles,
    useBoolean
} from 'hooks'
import { paths } from 'app/paths'

import { logToAnalytics, uiId, option, copyToClipboard } from 'modules'
import { InviteOptionsDialog } from './InviteOptionsDialog'

const useStyles = makeStyles(theme => ({
    title: {
        fontSize: '1.5rem',
        textAlign: 'center',
        fontWeight: theme.fontWeightBold
    },
    fieldTitle: {
        fontSize: '14px'
    },
    closeButton: {
        position: 'absolute',
        right: 0,
        top: 0,
        padding: theme.spacing(0.5),
        margin: theme.spacing(0.5)
    },
    actions: {
        padding: theme.spacing(3),
        justifyContent: 'center'
    },
    emailDescription: {
        color: '#78909c',
        fontSize: '14px'
    },
    description: {
        fontSize: '14px'
    },
    content: {
        height: '48vh'
    },
    counter: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
        color: '#78909c',
        fontWeight: 500
    },
    shareTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& button': {
            padding: 0,
            '&, &:hover': {
                background: 'transparent',
                textDecoration: 'underline'
            }
        }
    },
    copyLinkBox: {
        display: 'flex',
        alignItems: 'flex-end',
        marginTop: theme.spacing(1),
        '& input': {
            fontSize: theme.typography.pxToRem(14)
        },
        '& button': {
            marginLeft: theme.spacing(2)
        }
    },
    addInputButton: {
        padding: theme.spacing(1),
        margin: -theme.spacing(1),
        minWidth: 'auto',
        '&, &:hover': {
            boxShadow: 'none',
            background: 'transparent'
        }
    },
    formSection: {
        background: '#fff',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px'
    },
    shareLinkBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2, 3),
        background: '#fff',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px'
    }
}))

type RolesProps = {
    name: string
    level: number
}

const mappedRoles = ROLES_LIST.map(({ name, level }: RolesProps) => option(level + '', name))

const getAvailableRoles = (role: number) => mappedRoles.filter(({ value }) => role >= Number(value))

export const getInviteDefaults = (orgName: string, isPrivateAccount?: boolean) => ({
    message: `Hey,\n${
        isPrivateAccount ? `I am` : `Our team at ${orgName} is`
    } using Guidde to explain any software process with video. \nAs easy as taking a screenshot. Make sure you join ${
        isPrivateAccount ? 'my' : 'our'
    } space today and get access to unlimited Step-by-step videos 👇`,
    maxLength: 1500
})

type Props = { isOpen: boolean; onClose: () => void }

const generateEmailsObject = (emailsCount: number, value: any = '') =>
    Array(emailsCount)
        .fill(true)
        .reduce((o, _key, i) => Object.assign(o, { [`${i}`]: value }), {})

type ValuesType = {
    [key: number]: string
    permissions: number
    message: string
}

type InviteUserProps = {
    emails: Array<string>
    permissions: number
    message: string
    inviteTo?: string
}

const mapFormValues = (values: ValuesType) => {
    const { permissions, message, ...emailsKeys } = values

    const emails = Object.values<string>(emailsKeys).filter(Boolean)

    return {
        permissions,
        message,
        emails
    }
}

const validation = yup.string().email('Must be a valid email format')

export const InviteDialog = ({ isOpen, onClose }: Props) => {
    const classes = useStyles()
    const history = useHistory()

    const [emailsCount, setEmailsCount] = useState(1)
    const inviteTypeDialog = useBoolean()

    const { showSuccessNotification } = useNotification()

    const { isContentManager, isAdmin, isViewer } = useRoles()
    const { roles, orgId, uid, isPrivateAccount } = useAuth()
    const { name: orgName, domains } = useOrganization()

    const $invite = useDataMutation('/b/v1/userInvite', 'POST', {
        onSuccess: () => {
            showSuccessNotification('Invitation has been sent')
            onClose()

            history.push(paths.accountSettings, {
                isInviteRedirect: true
            })
        }
    })

    const $validateUserInvite = useDataMutation<
        { emails: Array<string> },
        {
            canInviteToMyOrg: Array<string>
        },
        Error
    >('/c/v1/validate-invite', 'POST')

    if (!orgName) return <LinearProgress />

    const inviteUser = ({ emails, message, permissions, inviteTo }: InviteUserProps) => {
        const inviteProps = { emails, permissions, message, ...(inviteTo && { inviteTo }) }

        return $invite.mutate(inviteProps).then(() =>
            logToAnalytics('send_invite', {
                inviteProps
            })
        )
    }

    const onFormSubmit = async (values: ValuesType) => {
        const { permissions, message, emails } = mapFormValues(values)

        if (isAdmin) {
            // admins can see extended invite options but only if they are valid emails
            const res = await $validateUserInvite.mutate({ emails })

            if (res?.canInviteToMyOrg.length) {
                return inviteTypeDialog.setTrue()
            }
        }

        return inviteUser({ emails, message, permissions })
    }

    const loading = $invite.isLoading || $validateUserInvite.isLoading

    const shareLink = `${location.origin}/join/${orgId}?origin=${uid}&referral=invite&invited_email=`

    const inviteDefaults = getInviteDefaults(orgName, isPrivateAccount)

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth={true}
            maxWidth="sm"
            PaperProps={{
                style: {
                    overflowY: 'hidden',
                    backgroundColor: 'transparent',
                    boxShadow: '0px 11px 15px -7px rgb(0 0 0 / 20%)'
                }
            }}
        >
            {loading && (
                <Box height="4px">
                    <LinearProgress />
                </Box>
            )}
            <IconButton className={classes.closeButton} onClick={onClose}>
                <CloseIcon />
            </IconButton>

            <Formik<ValuesType>
                onSubmit={onFormSubmit}
                isInitialValid={false}
                initialValues={{
                    permissions: isViewer
                        ? ROLE.VIEWER
                        : isContentManager
                        ? ROLE.CONTENT_MANAGER
                        : ROLE.MEMBER,
                    message: inviteDefaults.message,
                    ...generateEmailsObject(emailsCount)
                }}
                validationSchema={yup.object().shape({
                    message: yup.string().trim().nullable(),
                    ...generateEmailsObject(emailsCount),
                    ...Array(emailsCount)
                        .fill(true)
                        .map((_, i) => {
                            return i === 0
                                ? validation.nullable().required('Field is required')
                                : validation
                        })
                })}
            >
                {({ isValid, values }) => (
                    <Form className={classes.formSection}>
                        <DialogTitle>
                            <Typography className={classes.title}>Invite new teammates</Typography>
                        </DialogTitle>
                        <DialogContent dividers className={classes.content}>
                            {inviteTypeDialog.isTrue && $validateUserInvite.data && (
                                <InviteOptionsDialog
                                    emails={$validateUserInvite.data.canInviteToMyOrg}
                                    onClose={inviteTypeDialog.setFalse}
                                    onConfirm={inviteTo => {
                                        const { permissions, message, emails } =
                                            mapFormValues(values)

                                        inviteUser({ permissions, message, emails, inviteTo })
                                    }}
                                />
                            )}

                            <Box mt={1}>
                                {orgName && (
                                    <Typography className={classes.description}>
                                        {isPrivateAccount
                                            ? `Your teammates will receive an email with an invitation to
                                        join your Main and Default company spaces`
                                            : `Your teammates will receive an email with an invitation to
                                        join ${orgName}`}
                                    </Typography>
                                )}
                            </Box>
                            <Box mt={2}>
                                <Typography className={classes.fieldTitle}>
                                    <b>Email address(s)</b>
                                </Typography>
                                <Box mt={1}>
                                    {[...Array(emailsCount)].map((_, i) => (
                                        <Box key={i} mb={2}>
                                            <ConnectedInput
                                                data-cy="invite-dialog-email-address"
                                                variant="outlined"
                                                name={`${i}`}
                                                placeholder={
                                                    isPrivateAccount ? 'gmail.com' : `name@acme.com`
                                                }
                                                fullWidth
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                                <Box mt={2} display="flex" justifyContent="flex-start">
                                    <Button
                                        className={classes.addInputButton}
                                        title="Add one more email"
                                        onClick={() => setEmailsCount(pre => pre + 1)}
                                        color="default"
                                        disableRipple
                                    >
                                        <AddBoxIcon color="action" />
                                    </Button>
                                </Box>
                            </Box>
                            {!isPrivateAccount && (
                                <Box mt={2}>
                                    <Typography className={classes.fieldTitle}>
                                        <b>Role</b>
                                    </Typography>
                                    <ConnectedSelect
                                        name="permissions"
                                        options={getAvailableRoles(roles.a)}
                                        id={uiId('invite-new-teammates-permission-select')}
                                    />
                                </Box>
                            )}

                            <Box mt={2}>
                                <Typography className={classes.fieldTitle}>
                                    <b>Message</b>
                                </Typography>
                                <Box mt={1} position="relative">
                                    <ConnectedInput
                                        name="message"
                                        placeholder="Enter the message"
                                        multiline
                                        variant="outlined"
                                        fullWidth
                                        id={uiId('invite-new-teammates-message-input')}
                                        inputProps={{
                                            maxLength: inviteDefaults.maxLength,
                                            style: {
                                                marginBottom: '16px'
                                            }
                                        }}
                                    />
                                    <Typography className={classes.counter}>
                                        {values.message.length}/{inviteDefaults.maxLength}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions className={classes.actions}>
                            <Button
                                size="medium"
                                color="default"
                                onClick={onClose}
                                id={uiId('invite-new-teammates-btn-cancel')}
                            >
                                Cancel
                            </Button>

                            <Button
                                data-cy="invite-dialog-send-invitation"
                                size="medium"
                                type="submit"
                                disabled={loading || !isValid}
                                id={uiId('invite-new-teammates-btn-send')}
                            >
                                Send Invitation
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>

            <Box className={classes.shareLinkBox}>
                <Typography className={classes.fieldTitle}>
                    <b>Invite with link</b>
                </Typography>
                <Typography className={classes.fieldTitle}>
                    Anyone{' '}
                    {domains.length ? (
                        <>
                            with <b>@{domains.join(', @')}</b> email
                        </>
                    ) : (
                        ''
                    )}{' '}
                    can sign up using this link
                </Typography>

                <Box className={classes.copyLinkBox}>
                    <TextField type="text" value={shareLink} style={{ flexGrow: 1 }} />
                    <Button
                        onClick={() => {
                            copyToClipboard(shareLink).then(() => {
                                showSuccessNotification('Link copied to the clipboard')
                            })
                        }}
                        color="default"
                    >
                        Copy
                    </Button>
                </Box>
            </Box>
        </Dialog>
    )
}
